import { type OrderPreferencesState, SORT_OPTIONS } from '@choco-admin/types';
import { Card, CardBody } from '@heroui/react';
import React from 'react';

import { ChocoSelect } from '../../../../../../components/FormComponents';
import {
  isIntegrationLocked,
  useIntegrationSettingsAccess,
} from '../../../../../../featureFlags';
import { IntegrationLockNotice, LockedFieldTooltip } from '../../components';
import { useSupplierForm } from '../../SupplierFormProvider';
import { type TabProps } from '../../types';

import { PreferenceToggle } from './components';
import { DeliverySettingsSection } from './components/DeliverySettingsSection';
import { DEFAULT_FORM_VALUES } from './constants';

export const OrderPreferencesTab: React.FC<TabProps> = ({ mode, supplier }) => {
  const {
    formMethods: {
      watch,
      setValue,
      formState: { errors },
    },
  } = useSupplierForm();

  // Three of the settings on this tab are read by a distributor's integration,
  // and changing one of them on an integrated distributor has already stopped
  // live integrations. Once integrated they are read-only here for everyone,
  // including the people who may change them: the same settings appear on three
  // tabs, and one editable place is easier to reason about than four. That place
  // is the Settings tab.
  const access = useIntegrationSettingsAccess();
  const isIntegrated = supplier?.isIntegrated ?? false;
  const lockIntegrationSettings = isIntegrationLocked(access, isIntegrated, {
    isPuppy: supplier?.isPuppy,
    isTest: supplier?.isTest,
  });

  // In create mode or with supplier, always render with defaults
  return (
    <div className="space-y-6">
      {/* Order Preferences */}
      <Card>
        <CardBody className="space-y-6">
          <h2 className="text-lg font-semibold">Order Preferences</h2>
          <div className="space-y-4">
            <div className="space-y-4">
              {lockIntegrationSettings && (
                <IntegrationLockNotice
                  title="Locked by this distributor's integration"
                  message="The settings below decide what a buyer can put on an order, and this distributor's own system expects orders in the shape it was set up for. They are changed on the Settings tab instead. Everything else here can still be edited."
                />
              )}

              <LockedFieldTooltip
                isLocked={lockIntegrationSettings}
                canChange={access.canChange}
              >
                <PreferenceToggle
                  label="Allow custom products"
                  description="Let buyers add products that are not in your catalog"
                  isSelected={
                    watch('allowCustomProducts') ??
                    DEFAULT_FORM_VALUES.allowCustomProducts
                  }
                  onChange={(value) =>
                    setValue('allowCustomProducts', value, {
                      shouldDirty: true,
                    })
                  }
                  disabled={lockIntegrationSettings}
                />
              </LockedFieldTooltip>

              <LockedFieldTooltip
                isLocked={lockIntegrationSettings}
                canChange={access.canChange}
              >
                <PreferenceToggle
                  label="Mandatory customer number"
                  description="Require buyers to provide a customer number when placing orders"
                  isSelected={
                    watch('mandatoryCustomerNumber') ??
                    DEFAULT_FORM_VALUES.mandatoryCustomerNumber
                  }
                  onChange={(value) =>
                    setValue('mandatoryCustomerNumber', value, {
                      shouldDirty: true,
                    })
                  }
                  disabled={lockIntegrationSettings}
                />
              </LockedFieldTooltip>

              {/* No "Mandatory delivery date" toggle. A delivery date is
                  required for every distributor and cannot be turned off:
                  supplierService forces it true on read
                  (`handler/supplierGet.ts`, pinned by an integration test that
                  stores false and expects true). A switch here would accept the
                  change, save it, and come back on. */}

              <LockedFieldTooltip
                isLocked={lockIntegrationSettings}
                canChange={access.canChange}
              >
                <PreferenceToggle
                  label="Enable product-level comments"
                  description="Allow buyers to add comments to individual products"
                  isSelected={
                    watch('enableProductComments') ??
                    DEFAULT_FORM_VALUES.enableProductComments
                  }
                  onChange={(value) =>
                    setValue('enableProductComments', value, {
                      shouldDirty: true,
                    })
                  }
                  disabled={lockIntegrationSettings}
                />
              </LockedFieldTooltip>
            </div>

            <PreferenceToggle
              label="Allow fractional units"
              description="Allow buyers to order partial units (e.g., 0.5 kg)"
              isSelected={
                watch('allowFractionalUnits') ??
                DEFAULT_FORM_VALUES.allowFractionalUnits
              }
              onChange={(value) =>
                setValue('allowFractionalUnits', value, { shouldDirty: true })
              }
            />

            <PreferenceToggle
              label="Allow only one active variant"
              description="Restrict buyers to selecting only one variant per product"
              isSelected={
                watch('allowOnlyOneVariant') ??
                DEFAULT_FORM_VALUES.allowOnlyOneVariant
              }
              onChange={(value) =>
                setValue('allowOnlyOneVariant', value, { shouldDirty: true })
              }
            />

            <div>
              <p className="font-medium mb-2">Buyer cart sort</p>
              <ChocoSelect
                value={watch('sortBy') ?? DEFAULT_FORM_VALUES.sortBy}
                onChange={(e) =>
                  setValue(
                    'sortBy',
                    e.target.value as OrderPreferencesState['sortBy'],
                    { shouldDirty: true }
                  )
                }
                options={SORT_OPTIONS}
                className="max-w-xs"
                aria-label="Buyer cart sort order"
                label="Sort order"
                errorMessage={errors.sortBy?.message}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Time zone — the frame the delivery settings below are read in, so it
          sits with them rather than on the Settings tab. */}

      {/* Delivery Settings - Only show when supplier exists */}
      {supplier?.id && <DeliverySettingsSection supplierId={supplier.id} />}
    </div>
  );
};
